import React from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Button } from '@mui/material';

import { DescriptionsColumnTree } from '../models';
import { TreeItemLabel } from './tree-item-label';
import { DomainsDescriptionId } from '@models/feature-descriptor-domain';

interface DescriptionsTreeItemProps {
  descriptions: DescriptionsColumnTree['descriptions'];
}

const listLeftIndentation = '31px';

const DescriptionsVirtualList = ({
  descriptions,
}: DescriptionsTreeItemProps) => {
  const parentRef = React.useRef(null);

  const rowVirtualizer = useVirtualizer({
    count: descriptions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 25,
    overscan: 5,
  });

  return (
    <div
      ref={parentRef}
      style={{
        height: `${Math.min(25 * descriptions.length, 500)}px`,
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          position: 'relative',
        }}
      >
        {rowVirtualizer.getVirtualItems().map(virtualRow => (
          <div
            key={virtualRow.index}
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              left: listLeftIndentation,
              height: `${virtualRow.size}px`,
              transform: `translateY(${virtualRow.start}px)`,
            }}
          >
            <TreeItemLabel
              label={descriptions[virtualRow.index].description}
              nodeId={descriptions[virtualRow.index].nodeId}
              managedDescriptions={[descriptions[virtualRow.index]]}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const DescriptionsList = ({ descriptions }: DescriptionsTreeItemProps) => {
  return (
    <>
      {descriptions.map(description => (
        <TreeItemLabel
          key={DomainsDescriptionId.toString(description.id)}
          label={description.description}
          nodeId={description.nodeId}
          managedDescriptions={[description]}
        />
      ))}
    </>
  );
};

export const DescriptionsTreeItem = ({
  descriptions,
}: DescriptionsTreeItemProps) => {
  const [isVirtualListVisible, setIsVirtualListVisible] = React.useState(
    () => false
  );
  const [limitedDescriptions] = React.useState(() =>
    descriptions.length < 6 ? descriptions : descriptions.slice(0, 3)
  );

  const isWithShowMore = descriptions.length >= 6;

  return isVirtualListVisible ? (
    <DescriptionsVirtualList descriptions={descriptions} />
  ) : (
    <div style={{ marginLeft: listLeftIndentation }}>
      <DescriptionsList descriptions={limitedDescriptions} />
      {isWithShowMore ? (
        <Button
          sx={{ textTransform: 'lowercase', display: 'block' }}
          size="small"
          onClick={() => setIsVirtualListVisible(true)}
        >
          more
        </Button>
      ) : null}
    </div>
  );
};
